import { ReactElement, useMemo } from 'react';
import { useHasLockedRows, usePlanMeta, usePlanMutations } from '../state';
import { strategyMeta } from '../strategy';

import type { RequestFailure } from '@ff-it/api';
import type { PlanningConfig } from 'modules/campaign/block/types';
import { useMembership } from 'services';
import { PlanResponse, PlanStrategyType } from '../types';
import { Action, ActionDropdown } from 'components';
import { actionErrorOrThrow } from 'utilities';
import { useAtomValue } from 'jotai';
import { hasRowsAtom } from '../state/plan';

export function StrategyButton(): ReactElement {
  const { plan_strategy, type, planning } = usePlanMeta();
  const planningConfig: PlanningConfig = useMembership().planning_config;
  const hasRows = useAtomValue(hasRowsAtom);

  const { available_strategies: available, lock } = planningConfig[type];
  const hasLockedRows = useHasLockedRows();
  const disabled = lock || !planning || hasLockedRows;

  const { runMutation, setPlan } = usePlanMutations();

  const actions: Action[] = useMemo(() => {
    async function setStrategy(plan_strategy: PlanStrategyType) {
      if (hasRows && !window.confirm('Changing strategy may affect existing row data and cannot be undone. Proceed?')) {
        return;
      }

      try {
        const res = await runMutation<PlanResponse>(
          { method: 'POST', url: `plan/strategy/`, body: { plan_strategy } },
          {
            // !! This can't be optimistic since strategy columns and plan shapes have to be in sync
            // optimistic: (current) => ({ ...current, plan_strategy }),
            onSuccess: setPlan,
          },
        );
        res.unwrap();
      } catch (error) {
        actionErrorOrThrow(error as RequestFailure<unknown>);
      }
    }

    return available.map((value) => {
      const active = value === plan_strategy;
      return {
        action: value,
        button: {
          active,
          disabled: active,
          children: strategyMeta[value].title,
          onClick: () => setStrategy(value),
        },
      };
    });
  }, [available, plan_strategy, runMutation, setPlan, hasRows]);

  return (
    <ActionDropdown
      actions={actions}
      variant="secondary"
      testId="strategy-actions"
      size="sm"
      title="Plan strategy"
      disabled={disabled}
    >
      {strategyMeta[plan_strategy].title}
    </ActionDropdown>
  );
}
